import React from 'react';
import { Plus, Search, X, Settings, Maximize, Minimize, MonitorDown } from 'lucide-react';
import PromptCard from './prompts/PromptCard';
import { motion, AnimatePresence } from 'framer-motion';
import { Prompt, Variable, SortOption } from '../types';
import Button from './Button';
import IconButton from './IconButton';
import { useHybridTheme } from '../themes/useHybridTheme';
import SortMenu from './sort/SortMenu';
import Toast from './Toast';
import VariableForm from './VariableForm';
import { PremiumFeatureDialog } from './PremiumFeatureDialog';
import { extractVariables, replaceVariables } from '../utils/variables';
import { cn } from '../utils/cn';
import { checkFeatureEnabled } from '../utils/features';
import { Z_LAYERS } from '../constants/zLayers';
import { WEB_APP_URL } from '../constants/config';
import { getPlatform } from '../infrastructure/platform';

const isExtension = getPlatform().capabilities.extensionSurfaces;

interface PromptListProps {
  prompts: Prompt[];
  onAddPrompt: () => void;
  onEditPrompt: (prompt: Prompt) => void;
  onSelectPrompt: (prompt: Prompt) => void;
  onDeletePrompt: (prompt: Prompt) => void;
  isCollapsed?: boolean;
  isFullscreen?: boolean;
  selectedTags: string[];
  onSelectTag: (tag: string) => void;
  onClearTags: () => void;
  onToggleCollapse: () => void;
  currentPromptSort: SortOption;
  currentFolderSort: SortOption;
  onSortChange: (type: 'prompts' | 'folders', option: SortOption) => void;
}

export default function PromptList({
  prompts,
  onAddPrompt,
  onEditPrompt,
  onSelectPrompt,
  isCollapsed = false,
  isFullscreen = false,
  selectedTags,
  onSelectTag,
  onClearTags,
  onToggleCollapse: _onToggleCollapse,
  onDeletePrompt,
  currentPromptSort,
  currentFolderSort,
  onSortChange,
}: PromptListProps) {
  const { getClasses, getColor } = useHybridTheme();
  const [toastMessage, setToastMessage] = React.useState('');
  const [isToastVisible, setIsToastVisible] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [_expandedPromptId, _setExpandedPromptId] = React.useState<string | null>(null);
  const [showVariableForm, setShowVariableForm] = React.useState(false);
  const [selectedPrompt, setSelectedPrompt] = React.useState<Prompt | null>(null);
  const [variables, setVariables] = React.useState<string[]>([]);
  const [variableFormActionType, setVariableFormActionType] = React.useState<
    'copy' | 'openInChatGPT'
  >('copy');
  const [showPremiumDialog, setShowPremiumDialog] = React.useState(false);
  const [premiumFeature, setPremiumFeature] = React.useState('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const toastTimeoutRef = React.useRef<number>();

  const showToast = React.useCallback((message: string) => {
    setToastMessage(message);
    setIsToastVisible(true);
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      requestAnimationFrame(() => {
        setIsToastVisible(false);
      });
    }, 3000);
  }, []);

  // Listen for premium dialog messages
  React.useEffect(() => {
    const handleMessage = (message: any) => {
      if (message.type === 'SHOW_PREMIUM_DIALOG') {
        setPremiumFeature(message.feature);
        setShowPremiumDialog(true);
      }
    };

    return getPlatform().bus.subscribe(handleMessage);
  }, []);

  React.useEffect(() => {
    let isSubscribed = true;
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isSubscribed) return;
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        // Use setTimeout to ensure the focus happens after the current event cycle
        // This prevents the '/' character from being inserted into the search box
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      isSubscribed = false;
      document.removeEventListener('keydown', handleKeyPress);
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyPrompt = React.useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        showToast('Prompt copied to clipboard!');
      } catch (error) {
        console.error('Failed to copy prompt:', error);
        showToast('Failed to copy prompt to clipboard');
      }
    },
    [showToast]
  );

  const _handlePromptClick = React.useCallback(
    async (prompt: Prompt) => {
      const vars = extractVariables(prompt.text);
      if (vars.length > 0) {
        setVariables(vars);
        setSelectedPrompt(prompt);
        setShowVariableForm(true);
      } else {
        await handleCopyPrompt(prompt.text);
        onSelectPrompt(prompt);
      }
    },
    [handleCopyPrompt, onSelectPrompt]
  );

  const handleVariableSubmit = React.useCallback(
    async (values: Variable[]) => {
      if (!selectedPrompt) return;

      try {
        const formattedValues = values.map(v => ({
          name: v.name.trim(),
          value: v.value.trim(),
        }));

        const finalText = replaceVariables(selectedPrompt.text, formattedValues);
        if (finalText === selectedPrompt.text) {
          throw new Error('Variable replacement failed');
        }

        // Store the action type in a local variable to prevent race conditions
        const actionType = variableFormActionType;
        console.log('Variable form submitted with action type:', actionType);

        // Clear the form state first to prevent any side effects
        setShowVariableForm(false);
        setSelectedPrompt(null);
        setVariables([]);

        if (actionType === 'openInChatGPT') {
          // Open in ChatGPT with the completed variables
          console.log('Opening in ChatGPT with text:', finalText);
          const encodedPrompt = encodeURIComponent(finalText);

          // Use setTimeout to ensure this happens after state updates
          setTimeout(() => {
            window.open(`https://chat.openai.com/?model=gpt-4&q=${encodedPrompt}`, '_blank');
            showToast('Opening prompt in ChatGPT');
          }, 0);
        } else {
          // Only copy to clipboard if explicitly requested
          console.log('Copying to clipboard with text:', finalText);
          await handleCopyPrompt(finalText);
        }
      } catch (error) {
        console.error('Failed to process prompt:', error);
        showToast(error instanceof Error ? error.message : 'Failed to process prompt');

        // Clear form state on error as well
        setShowVariableForm(false);
        setSelectedPrompt(null);
        setVariables([]);
      }
    },
    [selectedPrompt, handleCopyPrompt, showToast, variableFormActionType]
  );

  const handleFullscreenToggle = async () => {
    if (isFullscreen) {
      void getPlatform().bus.send({ type: 'UI_ACTION', action: 'openPopup' });
      window.close();
    } else {
      // Check if the user has premium access before opening full screen view
      const hasAccess = await checkFeatureEnabled('fullScreenView');

      if (hasAccess) {
        // If the user has premium access, open the full screen view
        void getPlatform().bus.send({ type: 'UI_ACTION', action: 'openFullscreen' });
      } else {
        // If the user doesn't have premium access, show the premium dialog immediately
        setPremiumFeature('Full Screen View');
        setShowPremiumDialog(true);
      }
    }
  };

  const filteredPrompts = React.useMemo(
    () =>
      prompts.filter(prompt => {
        const terms = search
          .toLowerCase()
          .split(' ')
          .filter(term => term.trim() !== '');

        const searchTerms = terms.map(term => {
          const isNegative = term.startsWith('-');
          const actualTerm = isNegative ? term.slice(1) : term;

          let pattern = actualTerm
            .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            .replace(/\\\*/g, '.*')
            .replace(/\\\?/g, '.');

          return {
            isNegative,
            pattern: new RegExp(pattern, 'i'),
          };
        });

        const matchesSearch = searchTerms.every(({ isNegative, pattern }) => {
          const matches =
            pattern.test(prompt.title.toLowerCase()) ||
            pattern.test(prompt.description.toLowerCase()) ||
            prompt.tags.some(tag => pattern.test(tag.toLowerCase()));
          return isNegative ? !matches : matches;
        });

        const matchesTags =
          selectedTags.length === 0 || selectedTags.every(tag => prompt.tags.includes(tag));

        return matchesSearch && matchesTags;
      }),
    [prompts, search, selectedTags]
  );

  return (
    <div
      className={getClasses(
        `flex-1 flex flex-col pl-[10px] ${isFullscreen ? 'h-full' : 'h-[600px]'}`
      )}
    >
      <div
        className={getClasses('p-4 border-b border-gray-200 dark:border-gray-700 relative')}
        style={{ zIndex: Z_LAYERS.CONTENT }}
      >
        <div className="flex items-center space-x-4 relative" style={{ zIndex: Z_LAYERS.CONTENT }}>
          <div className="flex-1 flex items-center space-x-4">
            <div
              className="flex items-center space-x-2 relative"
              style={{ zIndex: Z_LAYERS.CONTENT }}
            >
              <motion.div className="flex-1 relative" whileHover={{ scale: 1.01 }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder='Search... (Press "/")'
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className={getClasses(
                    'w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 bg-transparent-light dark:bg-transparent-dark backdrop-blur-sm dark:text-white rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  )}
                  style={{
                    backgroundColor: getColor('promptCard-background'),
                    borderColor: getColor('promptCard-border'),
                    color: getColor('promptCard-text'),
                  }}
                  role="searchbox"
                  aria-label="Search prompts"
                  aria-expanded={search !== ''}
                />
                <Search
                  className={getClasses(
                    'w-5 h-5 text-gray-400 dark:text-gray-500 absolute left-3 top-2.5'
                  )}
                  aria-hidden="true"
                  role="presentation"
                />
                {search && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-2.5"
                    style={{ color: getColor('promptCard-text') }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Clear search"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                )}
              </motion.div>
              {search && (
                <Button
                  onClick={() => setSearch('')}
                  className={getClasses(
                    'flex items-center space-x-1 px-3 py-1.5 text-sm bg-transparent-light dark:bg-transparent-dark backdrop-blur-sm text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-100/80 dark:hover:bg-gray-700/80 border-2 border-red-500'
                  )}
                  variant="secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  //size="sm"
                  icon={<X className="w-4 h-4" />}
                >
                  Clear
                </Button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {selectedTags.length > 0 && (
                <motion.button
                  onClick={onClearTags}
                  className={getClasses(
                    'flex items-center space-x-1 px-3 py-1.5 text-sm bg-transparent-light dark:bg-transparent-dark backdrop-blur-sm text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-100/80 dark:hover:bg-gray-700/80 border-2 border-red-500'
                  )}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Clear selected tags"
                >
                  <span>Tags</span>
                  <X className="w-4 h-4" />
                </motion.button>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <SortMenu
              view={isFullscreen ? 'fullscreen' : 'main'}
              currentPromptSort={currentPromptSort}
              currentFolderSort={currentFolderSort}
              onSortChange={onSortChange}
            />
            <Button
              onClick={onAddPrompt}
              variant="primary"
              size="md"
              icon={<Plus className="w-4 h-4" />}
            >
              New
            </Button>

            {getPlatform().capabilities.settingsPage && (
              <IconButton
                icon={<Settings />}
                onClick={() => window.open('settings.html', '_blank')}
                aria-label="Open Settings"
              />
            )}

            {isExtension && (
              <>
                {/* <IconButton
                  icon={<ExternalLink />}
                  onClick={() => {
                    void getPlatform().bus.send({ type: 'UI_ACTION', action: 'openSidePanel' });
                  }}
                  aria-label="Toggle Side Panel"
                /> */}
                {/* Companion web app (ADR-012): same library, same account,
                    installable as a desktop/taskbar app — which extension
                    pages can never be. Shown on every extension surface that
                    renders this header (popup + fullscreen); the web app
                    itself doesn't advertise itself (isExtension gate). */}
                <IconButton
                  icon={<MonitorDown />}
                  onClick={() => void getPlatform().actions.openExternalUrl(WEB_APP_URL)}
                  aria-label="Get the desktop app"
                  title="Get the desktop app — your prompts on the web, installable to your taskbar"
                />

                <IconButton
                  icon={isFullscreen ? <Minimize /> : <Maximize />}
                  onClick={handleFullscreenToggle}
                  aria-label={isFullscreen ? 'Exit Fullscreen' : 'Open in Fullscreen'}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <Toast message={toastMessage} isVisible={isToastVisible} />

      <div
        className={getClasses(
          `flex-1 overflow-y-auto ${isFullscreen ? 'p-6' : isCollapsed ? 'p-2' : 'p-4'} scrollbar-custom`
        )}
        role="region"
        aria-label="Search results"
        aria-live="polite"
      >
        {filteredPrompts.length === 0 ? (
          <div
            className={getClasses('text-center text-gray-500 dark:text-gray-400 mt-8')}
            role="status"
            aria-label={`No prompts found${search ? ' matching your search' : ''}${selectedTags.length > 0 ? ' with selected tags' : ''}`}
          >
            No prompts found
          </div>
        ) : (
          <div
            className={cn(
              'flex flex-wrap gap-6',
              isFullscreen && 'children:flex-[1_1_400px] children:max-w-[calc(50%-12px)]',
              isCollapsed && 'children:flex-[1_1_calc(50%-12px)] children:max-w-[calc(50%-12px)]',
              !isFullscreen && !isCollapsed && 'children:flex-[1_1_100%]'
            )}
            role="status"
            aria-label={`Found ${filteredPrompts.length} prompt${filteredPrompts.length === 1 ? '' : 's'}${search ? ' matching your search' : ''}${selectedTags.length > 0 ? ' with selected tags' : ''}`}
          >
            {filteredPrompts.map(prompt => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                view={isFullscreen ? 'fullscreen' : 'main'}
                viewConfig={{
                  type: isFullscreen ? 'fullscreen' : 'main',
                  animation: {
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    transition: { duration: 0.3 },
                  },
                }}
                context={{
                  isSidebarCollapsed: isCollapsed,
                }}
                selectedTags={selectedTags}
                onSelectTag={onSelectTag}
                onEditPrompt={onEditPrompt}
                onDeletePrompt={onDeletePrompt}
                onSelectPrompt={onSelectPrompt}
                onShowVariableForm={(
                  prompt: Prompt,
                  vars?: string[],
                  actionType?: 'copy' | 'openInChatGPT'
                ) => {
                  if (vars) {
                    setVariables(vars);
                    setSelectedPrompt(prompt);
                    setVariableFormActionType(actionType || 'copy');
                    setShowVariableForm(true);
                  }
                }}
                onCopyToClipboard={async (text: string) => {
                  await handleCopyPrompt(text);
                }}
                themeConfig={{
                  variant: isCollapsed ? 'compact' : 'default',
                }}
                keyboardConfig={{
                  enableGlobalShortcuts: true,
                  shortcuts: {
                    copy: ['c'],
                    edit: ['e'],
                    delete: ['d'],
                  },
                }}
              />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showVariableForm && selectedPrompt && (
          <VariableForm
            promptId={selectedPrompt.id}
            variables={variables}
            onSubmit={handleVariableSubmit}
            onClose={() => {
              setShowVariableForm(false);
              setSelectedPrompt(null);
              setVariables([]);
            }}
            actionType={variableFormActionType}
          />
        )}
      </AnimatePresence>

      <PremiumFeatureDialog
        isOpen={showPremiumDialog}
        onClose={() => setShowPremiumDialog(false)}
        featureName={premiumFeature}
      />
    </div>
  );
}
